import { useState, useMemo, useCallback } from 'react'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { Phone, Mail, Check } from 'lucide-react'
import { contactCTAs } from './config'
import type { ChatMessageProps, ContactCTA } from '@/types/chatbot'

const URL_REGEX = /(https?:\/\/[^\s),]+|(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+(?:it|com|eu|org|net|io|dev)(?:\/[^\s),]*)?)/g
const BOLD_REGEX = /(\*\*[^*]+\*\*)/g

function parseInline(text: string, keyPrefix: string): React.ReactNode[] {
  // First split by URLs
  const urlParts = text.split(URL_REGEX)
  return urlParts.map((part, i) => {
    if (URL_REGEX.test(part)) {
      URL_REGEX.lastIndex = 0
      const href = part.startsWith('http') ? part : `https://${part}`
      return (
        <a
          key={`${keyPrefix}-url-${i}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:text-primary/80 transition-colors"
        >
          {part}
        </a>
      )
    }
    // Then parse bold within non-URL parts
    const boldParts = part.split(BOLD_REGEX)
    return boldParts.map((bp, j) => {
      if (bp.startsWith('**') && bp.endsWith('**')) {
        return <strong key={`${keyPrefix}-b-${i}-${j}`} className="font-semibold">{bp.slice(2, -2)}</strong>
      }
      return bp || null
    })
  })
}

const ctaIcons: Record<string, typeof Phone> = {
  phone: Phone,
  email: Mail,
}

function CopyButton({ cta }: { cta: ContactCTA }) {
  const { t } = useTranslation('common')
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(cta.value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback: do nothing
    }
  }, [cta.value])

  const Icon = copied ? Check : ctaIcons[cta.id] || Phone

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-500 hover:text-primary transition-colors"
    >
      <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
      <span>{copied ? (t('chatbot.cta.copied')) : t(cta.labelKey)}</span>
    </button>
  )
}

export function ChatMessage({ message, isTyping = false }: ChatMessageProps) {
  const isUser = message.role === 'user'

  const formattedContent = useMemo(() => {
    if (!message.content) return null
    return parseInline(message.content.trim(), message.id)
  }, [message.content])

  return (
    <div className={cn('flex flex-col', isUser ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-4 py-2.5',
          isUser
            ? 'bg-primary text-white rounded-br-sm'
            : 'bg-white text-gray-900 rounded-bl-sm shadow-sm'
        )}
      >
        {isTyping ? (
          <div className="flex gap-1.5 py-1 px-1">
            <span
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '0ms' }}
            />
            <span
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '150ms' }}
            />
            <span
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '300ms' }}
            />
          </div>
        ) : (
          <p className="text-sm whitespace-pre-wrap leading-relaxed m-0">
            {formattedContent}
          </p>
        )}
      </div>

      {/* Contact CTAs - copy to clipboard */}
      {!isUser && !isTyping && message.content && contactCTAs.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1.5 ml-1">
          {contactCTAs.map((cta) => (
            <CopyButton key={cta.id} cta={cta} />
          ))}
        </div>
      )}
    </div>
  )
}
